import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Sparkles, ArrowRight, ArrowLeft, X, Activity, GitBranch, Cpu, Wallet, Package,
  Newspaper, BarChart3, Key, ShieldCheck, Megaphone, Users, FileText, LinkIcon,
} from "lucide-react";
import { api } from "@/lib/api";

/**
 * Role-aware onboarding tour. Auto-shows on first dashboard visit (localStorage
 * flag per role). Skippable. Replaces the client-specific WelcomeTour for fresh
 * invited users — the original WelcomeTour still fires when ?welcome=1 is in
 * the URL (post-AI-qualification deep-link).
 *
 * iter86: client tour is now ORDER-AWARE — a buyer with zero orders gets a
 * different first step ("place your first order") than a buyer who already
 * has leads flowing ("see your live leads land here").
 */
const FLAG_PREFIX = "connexis_onboarding_seen_";

const CLIENT_STEPS_WITH_ORDERS = [
  {
    Icon: Activity,
    title: "Welcome to Connexis.",
    body: "This is where your inbound leads land in real-time. Every enquiry is AI-qualified, routed through your ping-tree and surfaced here within ~100ms.",
    cta: { label: "View live leads", to: "/dashboard/leads" },
  },
  {
    Icon: Wallet,
    title: "Prepaid wallet — no surprises.",
    body: "Top up your wallet with Stripe or Bank Transfer. Routed leads + AI system orders deduct automatically. Top up at /dashboard/billing.",
    cta: { label: "Open billing", to: "/dashboard/billing" },
  },
  {
    Icon: Cpu,
    title: "AI Systems on demand.",
    body: "Order outbound callers, qualification scripts and CRM integrations directly. Most systems are live within 48 hours of payment.",
    cta: { label: "Browse AI Systems", to: "/dashboard/ai-systems" },
  },
];

const CLIENT_STEPS_NO_ORDERS = [
  {
    Icon: Package,
    title: "Welcome to Connexis.",
    body: "Place your first lead order to kick things off — pick a sector, set your volume, choose delivery, and pay once. Your campaign goes live within 24 hours of cleared funds.",
    cta: { label: "Build your first order", to: "/dashboard/orders" },
  },
  {
    Icon: FileText,
    title: "Find your invoices anytime.",
    body: "Bank-transfer or Stripe — every invoice is downloadable from Billing. Pending orders show 'Awaiting transfer' until your funds clear; then they go live automatically.",
    cta: { label: "Open billing", to: "/dashboard/billing" },
  },
  {
    Icon: Activity,
    title: "Live leads land here next.",
    body: "Once your first order is funded, every inbound enquiry appears here in real time — AI-qualified, deduped, and routed in ~100ms.",
    cta: { label: "View leads page", to: "/dashboard/leads" },
  },
];

const STEPS_BY_ROLE = {
  client: CLIENT_STEPS_WITH_ORDERS, // default — overridden by order-awareness below
  publisher: [
    {
      Icon: Key,
      title: "Your publisher API key is ready.",
      body: "Copy it from the dashboard header. Send leads via POST /api/v1/leads/ingest with X-API-Key. You earn 90% on every accepted lead.",
      cta: { label: "Open dashboard", to: "/publisher" },
    },
    {
      Icon: Newspaper,
      title: "Push, watch, get paid.",
      body: "Every lead flows through the ping-tree. Accepted leads accrue to your balance in real-time. Request a payout any time — instant via Stripe Connect.",
      cta: { label: "View earnings", to: "/publisher" },
    },
    {
      Icon: ShieldCheck,
      title: "Connect Stripe for instant payouts.",
      body: "Set up Stripe Express (1 minute) to enable instant withdrawals. Until then, payouts arrive via Bank Transfer within 1–2 working days.",
      cta: { label: "Account settings", to: "/publisher/settings" },
    },
  ],
  affiliate: [
    {
      Icon: LinkIcon,
      title: "Welcome, partner.",
      body: "Grab your tracking links, share them with finance/SaaS buyers and earn recurring commission on every account you refer.",
      cta: { label: "My tracking links", to: "/affiliate/links" },
    },
    {
      Icon: BarChart3,
      title: "Track commissions in real-time.",
      body: "Every signup and reservation attributed to your link appears under Commissions. We pay out monthly via Stripe Connect.",
      cta: { label: "View commissions", to: "/affiliate/commissions" },
    },
    {
      Icon: ShieldCheck,
      title: "Set up payouts.",
      body: "Connect your Stripe Express account to enable instant payouts. Without it, commissions accrue but can't be released.",
      cta: { label: "Account settings", to: "/affiliate/settings" },
    },
  ],
  admin: [
    {
      Icon: GitBranch,
      title: "You're the control plane.",
      body: "Routing, buyers, campaigns, invoices, users and security all live here. Every action is logged to the audit trail.",
      cta: { label: "Ping trees", to: "/admin/routing" },
    },
    {
      Icon: Users,
      title: "Invite teammates anytime.",
      body: "Add admins, publishers, affiliates or client accounts at /admin/users. New users get a 48h emailed link to set their own password.",
      cta: { label: "Manage users", to: "/admin/users" },
    },
    {
      Icon: Megaphone,
      title: "Approve publishers + buyers.",
      body: "Inbound publisher applications appear at /admin/publishers — approving generates their API key. Buyers self-register or you invite them.",
      cta: { label: "Publishers", to: "/admin/publishers" },
    },
  ],
};

export default function OnboardingTour({ role }) {
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);
  const [steps, setSteps] = useState(STEPS_BY_ROLE[role] || STEPS_BY_ROLE.client);

  useEffect(() => {
    if (!role) return;
    const flag = `${FLAG_PREFIX}${role}`;
    const params = new URLSearchParams(window.location.search);
    if (params.get("welcome") === "1") return;
    // iter90r — `?skip_tour=1` bypasses the tour entirely (used by automation
    // and by the new Action-centre "skip" link). Sets the localStorage flag so
    // it stays dismissed on subsequent visits.
    if (params.get("skip_tour") === "1") {
      try { localStorage.setItem(flag, "1"); } catch (_) {}
      return;
    }
    if (localStorage.getItem(flag) === "1") return;

    let cancelled = false;
    (async () => {
      // iter86: for clients, decide which tour variant fits — fresh buyer
      // (no orders, no pending bank transfer) gets the "place your first order"
      // flow, otherwise the standard "your leads land here" flow.
      let nextSteps = STEPS_BY_ROLE[role] || STEPS_BY_ROLE.client;
      if (role === "client") {
        try {
          const { data } = await api.get("/client/lead-packs");
          const hasAny =
            (data?.active?.length || 0) +
            (data?.awaiting_payment?.length || 0) +
            (data?.completed?.length || 0) > 0;
          nextSteps = hasAny ? CLIENT_STEPS_WITH_ORDERS : CLIENT_STEPS_NO_ORDERS;
        } catch (_) {
          // fall back to defaults on error
        }
      }
      if (cancelled) return;
      setSteps(nextSteps);
      const t = setTimeout(() => setOpen(true), 700);
      return () => clearTimeout(t);
    })();
    return () => { cancelled = true; };
  }, [role]);

  const finish = () => {
    localStorage.setItem(`${FLAG_PREFIX}${role}`, "1");
    setOpen(false);
  };

  if (!open) return null;
  const cur = steps[step];
  if (!cur) return null;

  return (
    <div className="fixed inset-0 z-50 grid place-items-center p-4 bg-slate-900/40 backdrop-blur-sm animate-fade-up" data-testid="onboarding-tour" data-tour-role={role}>
      <div className="relative w-full max-w-lg rounded-3xl bg-white shadow-float overflow-hidden border border-slate-200">
        <button onClick={finish} className="absolute top-4 right-4 p-2 rounded-full hover:bg-slate-100 z-10" aria-label="Close tour" data-testid="tour-close">
          <X className="size-4" />
        </button>
        <div className="px-8 pt-8 pb-6">
          <div className="flex items-center gap-2 text-primary">
            <Sparkles className="size-4" />
            <span className="text-[11px] font-semibold tracking-[0.2em] uppercase">Step {step + 1} of {steps.length}</span>
          </div>
          <div className="mt-3 flex items-start gap-3">
            <span className="inline-grid place-items-center size-10 rounded-lg bg-slate-900 text-white shrink-0">
              <cur.Icon className="size-5" strokeWidth={1.6} />
            </span>
            <h2 className="text-2xl tracking-[-0.02em] font-semibold text-slate-900 leading-tight">{cur.title}</h2>
          </div>
          <p className="mt-5 text-[14.5px] text-slate-600 leading-relaxed">{cur.body}</p>
          {cur.cta && (
            <Link to={cur.cta.to} onClick={finish} className="mt-5 inline-flex items-center gap-1.5 text-[13.5px] font-medium text-[#0055FF] hover:underline" data-testid={`onboarding-cta-${step}`}>
              {cur.cta.label} <ArrowRight className="size-3.5" />
            </Link>
          )}
        </div>
        <div className="px-8 py-5 bg-slate-50/60 border-t border-slate-100 flex items-center justify-between">
          <div className="flex items-center gap-1.5">
            {steps.map((_, i) => (
              <span key={i} className={`h-1.5 rounded-full transition-all ${i === step ? "w-6 bg-primary" : i < step ? "w-1.5 bg-primary/60" : "w-1.5 bg-slate-300"}`} />
            ))}
          </div>
          <div className="flex items-center gap-2">
            {step > 0 && (
              <Button variant="ghost" size="sm" onClick={() => setStep(step - 1)} className="rounded-full" data-testid="onboarding-back">
                <ArrowLeft className="size-4" /> Back
              </Button>
            )}
            {step < steps.length - 1 ? (
              <Button size="sm" onClick={() => setStep(step + 1)} className="rounded-full px-4" data-testid="onboarding-next">
                Next <ArrowRight className="size-4" />
              </Button>
            ) : (
              <Button size="sm" onClick={finish} className="rounded-full px-4" data-testid="onboarding-finish">
                Got it — let's go <ArrowRight className="size-4" />
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
